import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Clock, Calendar, TrendingUp, Timer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAnalytics } from '../hooks/useAnalytics';
import { PieChart } from '../components/charts/PieChart';
import { BarChart } from '../components/charts/BarChart';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

type Preset = '7d' | '30d' | '3mo' | 'custom';

function getPresetRange(preset: Preset): { start: string; end: string } {
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  const start = new Date(end);

  switch (preset) {
    case '7d':
      start.setDate(start.getDate() - 7);
      break;
    case '30d':
      start.setDate(start.getDate() - 30);
      break;
    case '3mo':
      start.setMonth(start.getMonth() - 3);
      break;
    default:
      start.setDate(start.getDate() - 30);
  }

  start.setHours(0, 0, 0, 0);
  return { start: start.toISOString(), end: end.toISOString() };
}

export function AnalyticsPage() {
  const navigate = useNavigate();
  const [preset, setPreset] = useState<Preset>('30d');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const range = useMemo(() => {
    if (preset === 'custom' && customStart && customEnd) {
      return {
        start: new Date(customStart).toISOString(),
        end: new Date(customEnd + 'T23:59:59.999Z').toISOString(),
      };
    }
    return getPresetRange(preset);
  }, [preset, customStart, customEnd]);

  const { data, loading, error } = useAnalytics(range.start, range.end);

  const pieData = useMemo(
    () =>
      (data?.hoursPerCalendar ?? []).map((c) => ({
        label: c.calendarName,
        value: c.hours,
        color: c.calendarColor,
      })),
    [data],
  );

  const weeklyData = useMemo(
    () =>
      (data?.hoursPerWeek ?? []).map((w) => {
        const d = new Date(w.weekStart);
        return {
          label: `${d.getMonth() + 1}/${d.getDate()}`,
          value: w.hours,
        };
      }),
    [data],
  );

  const dowData = useMemo(() => {
    const all = Array.from({ length: 7 }, (_, i) => ({
      label: DAY_LABELS[i],
      value: 0,
    }));
    for (const d of data?.hoursPerDayOfWeek ?? []) {
      all[d.dayOfWeek].value = d.hours;
    }
    return all;
  }, [data]);

  return (
    <div className="flex flex-col h-full overflow-auto">
      {/* Header */}
      <div className="flex items-center gap-3 border-b px-4 py-3">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => navigate('/a/arche_calendar')}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-lg font-semibold">Time Analytics</h1>
      </div>

      <div className="p-4 space-y-6 max-w-4xl mx-auto w-full">
        {/* Date range presets */}
        <div className="flex items-center gap-2 flex-wrap">
          {(['7d', '30d', '3mo'] as const).map((p) => (
            <Button
              key={p}
              variant={preset === p ? 'secondary' : 'outline'}
              size="sm"
              onClick={() => setPreset(p)}
            >
              {p === '7d' ? 'Last 7 days' : p === '30d' ? 'Last 30 days' : 'Last 3 months'}
            </Button>
          ))}
          <Button
            variant={preset === 'custom' ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => setPreset('custom')}
          >
            Custom
          </Button>

          {preset === 'custom' && (
            <div className="flex items-center gap-2 ml-2">
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="border rounded px-2 py-1 text-sm"
              />
              <span className="text-muted-foreground text-sm">to</span>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="border rounded px-2 py-1 text-sm"
              />
            </div>
          )}
        </div>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {error && <div className="text-destructive text-center py-8">{error}</div>}

        {data && !loading && (
          <>
            {/* Summary cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <SummaryCard
                icon={<Calendar className="h-4 w-4" />}
                label="Total Events"
                value={String(data.summary.totalEvents)}
              />
              <SummaryCard
                icon={<Clock className="h-4 w-4" />}
                label="Total Hours"
                value={`${data.summary.totalHours}h`}
              />
              <SummaryCard
                icon={<TrendingUp className="h-4 w-4" />}
                label="Avg Daily"
                value={`${data.summary.avgDailyHours}h`}
              />
              <SummaryCard
                icon={<Timer className="h-4 w-4" />}
                label="Avg Duration"
                value={`${data.summary.avgEventDurationMinutes}m`}
              />
            </div>

            {/* Charts */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Hours by calendar */}
              <div className="border rounded-lg p-4">
                <h3 className="text-sm font-semibold mb-4">Hours by Calendar</h3>
                <PieChart data={pieData} />
              </div>

              {/* Busiest day of week */}
              <div className="border rounded-lg p-4">
                <h3 className="text-sm font-semibold mb-4">Hours by Day of Week</h3>
                <BarChart data={dowData} barColor="#6366f1" />
              </div>
            </div>

            {/* Weekly load chart (full width) */}
            <div className="border rounded-lg p-4">
              <h3 className="text-sm font-semibold mb-4">Weekly Load</h3>
              <BarChart data={weeklyData} height={200} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function SummaryCard({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="border rounded-lg p-3">
      <div className="flex items-center gap-1.5 text-muted-foreground text-xs mb-1">
        {icon}
        {label}
      </div>
      <div className="text-2xl font-semibold">{value}</div>
    </div>
  );
}
